import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import vars from '@/config/vars';
import { resetUser } from '@/data/features/user/user';

/**
 * get base url
 * @param {string} val
 * @returns {string}
 */
const getBaseurl = (val = '') => `${vars.request.baseUrl}/${val}`;

const fetchBaseQueryInstance = fetchBaseQuery({
  baseUrl: vars.request.baseUrl,
  timeout: vars.request.timeout,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const { accessToken, refreshToken } = getState().user;
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
      headers.set('X-Refresh-Token', refreshToken);
    }
    return headers;
  },
});

const api = createApi({
  reducerPath: 'api',
  baseQuery: async (args, apiContext, extraOptions) => {
    const result = await fetchBaseQueryInstance(args, apiContext, extraOptions);

    if (result?.error?.status === 401) {
      apiContext.dispatch(resetUser());
    }

    return result;
  },

  endpoints: (builder) => ({
    save: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/createOrUpdate'),
        method: 'POST',
        body,
      }),
    }),
    loanOptions: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/loan/getByBorrowerAge'),
        method: 'POST',
        body,
      }),
    }),
    calculate: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/calculateForMaxPeriodWithoutBankMixDetailsByDetails'),
        method: 'POST',
        body,
      }),
    }),
    calculateDisposalboard: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/calculateForKnownPeriodAndRiskByDetails'),
        method: 'POST',
        body,
      }),
    }),
    sendingToTheCustomerForSignature: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/email/sendSummaryLinkToClient'),
        method: 'POST',
        body,
      }),
    }),
    sendApproveLinkToClient: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/email/sendApproveLinkToClient'),
        method: 'POST',
        body,
      }),
    }),
    getClientById: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/client/getById'),
        method: 'POST',
        body,
      }),
    }),
    downloadPdf: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/pdf/createById'),
        method: 'POST',
        body,
      }),
    }),
    ocrPdfFile: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/pdf/ocrPdfFile'),
        method: 'POST',
        body,
      }),
    }),
    ocrPdfFileAndCompare: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/pdf/ocrPdfFileAndCompare'),
        method: 'POST',
        body,
      }),
    }),
    compareloanFromPdfFile: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/pdf/compareloanFromPdfFile'),
        method: 'POST',
        body,
      }),
    }),
    calculateAmount: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/reverseMortgage/calculatePercentage'),
        method: 'POST',
        body,
      }),
    }),
    calculateCycleMortgageWithSamePeriod: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/mortgage/calculateCycleMortgageWithSamePeriod'),
        method: 'POST',
        body,
      }),
    }),
    calculateCycleMortgageWithLongerPeriod: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/mortgage/calculateCycleMortgageWithLongerPeriod'),
        method: 'POST',
        body,
      }),
    }),
    calculateCycleMortgageWithShorterPeriod: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/mortgage/calculateCycleMortgageWithShorterPeriod'),
        method: 'POST',
        body,
      }),
    }),
    calculateCycleMortgageRepaymentTable: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/mortgage/calculateCycleMortgageRepaymentTable'),
        method: 'POST',
        body,
      }),
    }),
    reverseMortgageCalculate: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/calculateReverseMortgageDataByDetails'),
        method: 'POST',
        body,
      }),
    }),
    reverseMortgageCalculateMaxLoan: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/calculateReverseMortgageMaxLoanByDetails'),
        method: 'POST',
        body,
      }),
    }),
    getTranchesData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/tranches/getAllTranches'),
        method: 'POST',
        body,
      }),
    }),
    updateTranchesData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings//tranches/updateTranches'),
        method: 'POST',
        body,
      }),
    }),
    getCurrentMixData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/mix/getCurrent'),
        method: 'POST',
        body,
      }),
    }),
    addCurrentMortgageCycleMixData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/mortgageCycleMix/update'),
        method: 'POST',
        body,
      }),
    }),
    getCurrentMortgageCycleMixData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/mortgageCycleMix/getCurrent'),
        method: 'POST',
        body,
      }),
    }),
    addCurrentMixData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/mix/update'),
        method: 'POST',
        body,
      }),
    }),
    getReverseMortgageData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/reverseMortgage/getAll'),
        method: 'POST',
        body,
      }),
    }),
    updateReverseMortgageData: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/settings/reverseMortgage/updatePercentage'),
        method: 'POST',
        body,
      }),
    }),
    userLogin: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/auth/userLogin'),
        method: 'POST',
        body,
      }),
    }),
    leadContact: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/lead'),
        method: 'POST',
        body,
      }),
    }),
    reverseMortgageContact: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/reverseMortgage'),
        method: 'POST',
        body,
      }),
    }),
    existingMortgageContact: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/existingMortgage'),
        method: 'POST',
        body,
      }),
    }),
    problemConduct: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/problem'),
        method: 'POST',
        body,
      }),
    }),
    businessCreditContact: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/businessCredit'),
        method: 'POST',
        body,
      }),
    }),
    mortgageRefinancingContact: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/contact/mortgageRefinancing'),
        method: 'POST',
        body,
      }),
    }),
    getAllLead: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/lead/getAll'),
        method: 'POST',
      }),
    }),
    getLeadFile: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/lead/getFile'),
        method: 'POST',
        body,
      }),
    }),
    getAllRoles: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/role/getAll'),
        method: 'POST',
      }),
    }),
    createUser: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/user/create'),
        method: 'POST',
        body,
      }),
    }),
    getAllUsers: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/user/getAll'),
        method: 'POST',
      }),
    }),
    getUserById: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/user/getById'),
        method: 'POST',
        body,
      }),
    }),
    updateUser: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/user/update'),
        method: 'POST',
        body,
      }),
    }),
    getAllSupportType: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/supportType/getAll'),
        method: 'POST',
      }),
    }),
    getAllOffers: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/getAll'),
        method: 'POST',
        body,
      }),
    }),
    getAllRates: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/rate/getAll'),
        method: 'POST',
      }),
    }),
    updateRate: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/rate/updateReverseMortgage'),
        method: 'POST',
        body,
      }),
    }),
    getOfferById: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/getById'),
        method: 'POST',
        body,
      }),
    }),
    getSummeryfile: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/getSummeryFile'),
        method: 'POST',
        body,
      }),
    }),
    getUserStatus: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/user/getStatus'),
        method: 'POST',
        body,
      }),
    }),
    userLogout: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/auth/logout'),
        method: 'POST',
        body,
      }),
    }),
    newOffer: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/new'),
        method: 'POST',
        body,
      }),
    }),
    getClientByIdNumber: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/client/getByIdNumber'),
        method: 'POST',
        body,
      }),
    }),
    updateClient: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/client/update'),
        method: 'POST',
        body,
      }),
    }),
    checkAuth: builder.mutation({
      query: () => ({
        url: getBaseurl('v1/auth/checkAuth'),
        method: 'POST',
      }),
    }),
    pingAuth: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/auth/pingAuth'),
        method: 'POST',
        body,
      }),
    }),
    getPaymentLink: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/payment/createLowProfile'),
        method: 'POST',
        body,
      }),
    }),
    finishProcess: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/payment/webhook'),
        method: 'POST',
        body,
      }),
    }),
    getAllClients: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/client/getAll'),
        method: 'POST',
        body,
      }),
    }),
    updateOfferStatusToPaidAndGeneratedPdf: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/updateStatusToPaidAndGeneratedPdf'),
        method: 'POST',
        body,
      }),
    }),
    saveCustomizedMixes: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/customizedMixes/save'),
        method: 'POST',
        body,
      }),
    }),
    checkMortgageEligibility: builder.mutation({
      query: (body) => ({
        url: getBaseurl('v1/offer/checkMortgageEligibility'),
        method: 'POST',
        body,
      }),
    }),
  }),
});
export default api;

export const {
  useSaveMutation,
  useLoanOptionsMutation,
  useCalculateMutation,
  useCalculateDisposalboardMutation,
  useDownloadPdfMutation,
  useOcrPdfFileMutation,
  useOcrPdfFileAndCompareMutation,
  useCalculateAmountMutation,
  useCalculateCycleMortgageWithSamePeriodMutation,
  useCalculateCycleMortgageWithShorterPeriodMutation,
  useCalculateCycleMortgageWithLongerPeriodMutation,
  useCalculateCycleMortgageRepaymentTableMutation,
  useReverseMortgageCalculateMutation,
  useReverseMortgageCalculateMaxLoanMutation,
  useSendingToTheCustomerForSignatureMutation,
  useGetTranchesDataMutation,
  useUpdateTranchesDataMutation,
  useGetCurrentMixDataMutation,
  useAddCurrentMixDataMutation,
  useGetCurrentMortgageCycleMixDataMutation,
  useAddCurrentMortgageCycleMixDataMutation,
  useGetReverseMortgageDataMutation,
  useUserLoginMutation,
  useUserLogoutMutation,
  useLeadContactMutation,
  useReverseMortgageContactMutation,
  useGetAllLeadMutation,
  useGetLeadFileMutation,
  useGetAllRolesMutation,
  useCreateUserMutation,
  useGetAllUsersMutation,
  useGetUserByIdMutation,
  useUpdateUserMutation,
  useUpdateReverseMortgageDataMutation,
  useGetAllSupportTypeMutation,
  useGetAllOffersMutation,
  useGetSummeryfileMutation,
  useGetUserStatusMutation,
  useGetOfferByIdMutation,
  useGetAllRatesMutation,
  useUpdateRateMutation,
  useBusinessCreditContactMutation,
  useExistingMortgageContactMutation,
  useCompareloanFromPdfFileMutation,
  useNewOfferMutation,
  useGetClientByIdNumberMutation,
  useUpdateClientMutation,
  useProblemConductMutation,
  useMortgageRefinancingContactMutation,
  useCheckAuthMutation,
  usePingAuthMutation,
  useSendApproveLinkToClientMutation,
  useGetClientByIdMutation,
  useGetPaymentLinkMutation,
  useFinishProcessMutation,
  useGetAllClientsMutation,
  useUpdateOfferStatusToPaidAndGeneratedPdfMutation,
  useSaveCustomizedMixesMutation,
  useCheckMortgageEligibilityMutation,
} = api;
